import { t } from "@web-monorepo/i18n/macro";
import { StoreWithProps, useClassState } from "@web-monorepo/hooks";
import { useEffect } from "react";
import type { RadioChannel, SendRadioProps } from "src/pods/radio";
import logEvent from "src/utils/logEvent";

export type UseRadioDisplayProps = {
  channel?: RadioChannel;
  currentTrackIndex?: number;
  playing?: boolean;
  sendRadio?: (params: SendRadioProps) => void;
  displayClose?: VoidFunction;
};

export type UseRadioDisplayResult = {
  channel?: RadioChannel;
  currentTrackIndex?: number;
  playing?: boolean;
  hasAudioSrc: boolean;
  audioSrc?: string;
  caption: string;
  togglePlayback: () => void;
  nextTrack: () => void;
  previousTrack: () => void;
  onAudioPlaying: () => void;
  onAudioPause: () => void;
  onAudioEnded: () => void;
};

/**
 * Valtio-backed store for RadioDisplay's playback control. All reactive state
 * lives in the parent toolkit state store (via props); this VM owns lifecycle
 * (start/stop log + sendRadio teardown) and derives the audio src + caption.
 */
class RadioDisplayStore extends StoreWithProps<UseRadioDisplayProps> {
  get hasAudioSrc(): boolean {
    const { channel, currentTrackIndex } = this.props;
    return Boolean(channel && typeof currentTrackIndex === "number" && channel.tracks[currentTrackIndex].src);
  }

  get audioSrc(): string | undefined {
    const { channel, currentTrackIndex } = this.props;
    if (!channel || currentTrackIndex === undefined) return undefined;
    return channel.tracks[currentTrackIndex].src;
  }

  get caption(): string {
    if (!this.audioSrc) return "";
    return this.audioSrc.includes("calm") ? t`[relaxing instrumental music]` : t`[upbeat instrumental music]`;
  }

  start() {
    logEvent({ eventName: "radio.result.open" });
  }

  stop() {
    this.props.sendRadio?.({ channel: this.props.channel });
    this.props.displayClose?.();
    logEvent({ eventName: "radio.result.close" });
  }

  togglePlayback() {
    this.props.sendRadio?.({
      currentTrackIndex: this.props.currentTrackIndex,
      playing: !this.props.playing,
      channel: this.props.channel,
    });
  }

  nextTrack() {
    const { sendRadio, currentTrackIndex, channel } = this.props;
    if (channel && currentTrackIndex !== undefined) {
      sendRadio?.({
        channel,
        currentTrackIndex: (currentTrackIndex + 1) % channel.tracks.length,
        playing: true,
      });
    }
  }

  previousTrack() {
    const { sendRadio, currentTrackIndex, channel } = this.props;
    if (channel && currentTrackIndex !== undefined) {
      sendRadio?.({
        channel,
        currentTrackIndex: (currentTrackIndex + (channel.tracks.length - 1)) % channel.tracks.length,
        playing: true,
      });
    }
  }

  onAudioPlaying() {
    if (!this.props.playing) {
      this.props.sendRadio?.({
        channel: this.props.channel,
        currentTrackIndex: this.props.currentTrackIndex,
        playing: true,
      });
    }
  }

  onAudioPause() {
    if (this.props.playing) {
      this.props.sendRadio?.({
        channel: this.props.channel,
        currentTrackIndex: this.props.currentTrackIndex,
        playing: false,
      });
    }
  }

  onAudioEnded() {
    this.nextTrack();
  }
}

export function useRadioDisplay(props: UseRadioDisplayProps): UseRadioDisplayResult {
  const store = useClassState(RadioDisplayStore, props);
  const { start, stop } = store;

  // Mount-only lifecycle. The `start`/`stop` methods are stable references on
  // the underlying Valtio proxy (bound in useClassState), so this effect runs
  // once per mount even though `store` itself is a fresh Proxy each render.
  useEffect(() => {
    start();
    return () => stop();
  }, [start, stop]);

  return {
    channel: store.props.channel,
    currentTrackIndex: store.props.currentTrackIndex,
    playing: store.props.playing,
    hasAudioSrc: store.hasAudioSrc,
    audioSrc: store.audioSrc,
    caption: store.caption,
    togglePlayback: store.togglePlayback,
    nextTrack: store.nextTrack,
    previousTrack: store.previousTrack,
    onAudioPlaying: store.onAudioPlaying,
    onAudioPause: store.onAudioPause,
    onAudioEnded: store.onAudioEnded,
  };
}
